import React from "react";
import LegalDocumentPage from "./LegalDocumentPage";

const LAST_UPDATED = "10 July 2026";

const PrivacyPolicyPage = () => (
  <LegalDocumentPage
    eyebrow="Legal"
    title="Privacy Policy"
    summary="InfoBoxx helps people in Ghana understand real-life decisions — from land and legal matters to health, careers, nutrition, and responsible gaming — then connect with verified providers when they are ready. This policy explains how we collect, use, and protect your information while you use that journey."
    lastUpdated={LAST_UPDATED}
    relatedLinks={[
      { to: "/terms", label: "Terms & Conditions" },
      { to: "/cookies", label: "Cookie Policy" },
    ]}
    sections={[
      {
        heading: "1. Who we are",
        paragraphs: [
          "InfoBoxx is operated from Accra, Ghana. We provide category-based guides, articles, media, and tools that help consumers ask better questions and take clearer next steps. When you request expert help, we may share relevant contact details with a verified partner so they can respond to your request.",
          "If you have privacy questions, contact us at info@infoboxx.com or through our Contact page.",
        ],
      },
      {
        heading: "2. Information we collect",
        paragraphs: [
          "We only collect what we need to run the platform, personalise your experience, and connect you with the right support.",
        ],
        bullets: [
          "Account details such as name, email, phone number, and location when you register or update your profile.",
          "Interest preferences — the categories and subcategories you choose (for example Legal · Land & Property Law) so we can show relevant content and gate topic libraries fairly.",
          "Usage information such as pages viewed, topics opened, search terms on category pages, and how you move through Ask · Learn · Talk · Book journeys.",
          "Communication details when you contact us, book an appointment, or request to speak with a provider.",
          "Technical data such as device type, browser, IP address, and approximate location needed for security and service reliability.",
        ],
      },
      {
        heading: "3. How we use your information",
        paragraphs: [
          "We use your information to deliver InfoBoxx’s core promise: answers first, qualified action next.",
        ],
        bullets: [
          "Create and secure your account, including OTP-based sign-in and verification.",
          "Personalise category hubs, topic pages, articles, and media based on your interests.",
          "Route high-intent requests (WhatsApp, callbacks, appointments) to verified partners in the relevant category.",
          "Improve content quality, product features, and partner matching across Legal, Real Estate, Health, Careers, Nutrition, and Betting & Gaming.",
          "Send service messages about your account, security, or requests you initiated. Marketing messages are only sent where allowed and can be stopped on request.",
        ],
      },
      {
        heading: "4. Sharing with partners and providers",
        paragraphs: [
          "InfoBoxx is not a marketplace that sells your data indiscriminately. When you ask to talk to an expert, book support, or otherwise request follow-up, we share the information needed for that partner to help you — typically your name, contact details, topic of interest, and a short summary of your request.",
          "Verified partners are expected to use that information only to respond to your enquiry and to follow applicable law. We do not sell personal data to third parties for unrelated advertising.",
        ],
      },
      {
        heading: "5. Legal bases and retention",
        paragraphs: [
          "We process personal data to perform our contract with you (providing the service), to pursue legitimate interests such as platform security and product improvement, and where required by law. Where consent is needed — for example certain cookies or optional marketing — we will ask for it.",
          "We keep account and interest data while your account is active and for a reasonable period afterwards for security, dispute resolution, and legal compliance. Enquiry records shared with partners may be retained by those partners under their own policies.",
        ],
      },
      {
        heading: "6. Your choices and rights",
        paragraphs: [
          "You can update profile details and manage topic interests from your account. You may also request access to, correction of, or deletion of personal data we hold about you, subject to legal exceptions.",
          "To exercise these rights, email info@infoboxx.com with enough detail for us to verify your request. We will respond within a reasonable time.",
        ],
      },
      {
        heading: "7. Security and children",
        paragraphs: [
          "We use administrative and technical safeguards appropriate to a consumer platform handling account and lead data. No online service is perfectly secure; please protect your devices and do not share OTP codes.",
          "InfoBoxx is intended for adults and older teens making consumer decisions. We do not knowingly collect personal data from young children. Betting & Gaming content is for users of legal age under Ghanaian law.",
        ],
      },
      {
        heading: "8. Changes to this policy",
        paragraphs: [
          "We may update this Privacy Policy as InfoBoxx grows. Material changes will be reflected on this page with a revised “Last updated” date. Continued use of the platform after changes means you accept the updated policy.",
        ],
      },
    ]}
  />
);

export default PrivacyPolicyPage;
